/**
 * Trust Score – Personal Dashboard placeholder.
 * Displays stub data from GET /api/v1/trust-score/me until full calculation is implemented.
 */
import React, { useEffect, useState } from 'react';
import { trustScoreApi } from '../services/api';

interface TrustScoreData {
  score: number;
  level: string;
  weightMultiplier?: number;
  factors: unknown[];
  message?: string;
}

const TrustScore: React.FC = () => {
  const [data, setData] = useState<TrustScoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await trustScoreApi.getMe();
      if (cancelled) return;
      if (res?.success && res?.data) setData(res.data);
      else setError(res?.error || 'Failed to load trust score');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading trust score…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <div style={{ color: '#c00', marginBottom: '16px' }}>{error}</div>
        <p style={{ color: '#666' }}>Sign in to view your trust score, or try again later.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>Trust Score</h1>
        <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
          Your reputation and weight in the Mango ecosystem.
        </p>
      </div>
      {data && (
        <>
          <div style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '32px', fontWeight: 700, color: '#1e293b' }}>{data.score}</span>
              <span style={{ fontSize: '18px', color: '#64748b' }}>{data.level}</span>
              {data.weightMultiplier != null && (
                <span style={{ fontSize: '13px', color: '#94a3b8' }}>Weight ×{data.weightMultiplier}</span>
              )}
            </div>
            {data.message && (
              <p style={{ margin: '12px 0 0 0', fontSize: '14px', color: '#64748b' }}>{data.message}</p>
            )}
          </div>
          {Array.isArray(data.factors) && data.factors.length > 0 && (
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#475569' }}>Factors</h3>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {data.factors.map((f: unknown, i: number) => (
                  <li key={i} style={{ fontSize: '13px', color: '#64748b' }}>{String(f)}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrustScore;
